import type { IMonster } from './monster.interface';
// 레이드 유형
export const RAID_TYPE = {
  COOPERATIVE: 'COOPERATIVE', // 협력형
  SEQUENTIAL: 'SEQUENTIAL', // 순서 지정형
  MASS_RAID: 'MASS_RAID', // 마스레이드형
  TIMELINE: 'TIMELINE', // 타임라인형
} as const;

export type RAID_TYPE_VALUE = typeof RAID_TYPE[keyof typeof RAID_TYPE];

// 레이드 상태
export const RAID_STATUS = {
  SCHEDULED: 'SCHEDULED', // 예약됨
  IN_PROGRESS: 'IN_PROGRESS', // 진행중
  COMPLETED: 'COMPLETED', // 완료됨
} as const;

export type RAID_STATUS_VALUE = typeof RAID_STATUS[keyof typeof RAID_STATUS];

export const DEFAULT_INVESTIGATION_RAID_ENCOUNTER_TITLE = '몬스터 조우';
export const DEFAULT_INVESTIGATION_RAID_ENCOUNTER_MESSAGE =
  '{monsterName} 출현. 몬스터 조우가 시작되었습니다.';

// 레이드 인터페이스
export interface IRaid {
  monsterSnapshots?: IMonster[];
  startSnapshot?: Pick<IRaid, 'monsterIds' | 'monsterAttacks' | 'turnTimeLimit' | 'timeLimit' | 'raidType'>;
  id: string;
  title: string; // 레이드 이름
  raidType: RAID_TYPE_VALUE; // 레이드 유형
  status: RAID_STATUS_VALUE; // 레이드 상태
  thumbnailURL?: string; // 레이드 썸네일
  backgroundURL?: string; // 레이드 배경 이미지 (상세 페이지 전체 배경)
  investigationEncounterTitle?: string; // 조사 결과 레이드 제목 문구
  investigationEncounterMessage?: string; // 조사 결과 레이드 안내 문구
  monsterAttacks: boolean; // 몬스터의 공격 여부
  monsterIds: string[]; // 출몰 몬스터 ID 목록
  description?: string; // 레이드 설명
  timeLimit?: number | null; // 레이드 제한시간 (분, null이면 무제한)
  scheduledStartTime?: Date | null; // 레이드 시작 예약 시간
  startedAt?: Date | null; // 실제 시작 시간
  completedAt?: Date | null; // 완료 시간
  participantCount?: number; // 참가자 수
  participants?: string[]; // 참가자 userId 배열
  participantNames?: Record<string, string>;   // userId → 캐릭터명
  participantPhotos?: Record<string, string>;  // userId → 썸네일 URL
  participantSpeeds?: Record<string, number>;  // userId → 속도 스탯
  maxBossHp?: number; // 보스 최대 HP
  currentBossHp?: number; // 보스 현재 HP
  order: number; // 정렬 순서
  createdAt?: Date;
  updatedAt?: Date;
  authorUid?: string;
  isActive?: boolean; // 활성화 여부

  // ── 확장 필드 (PRD 6.5) ────────────────────────────
  turnTimeLimit?: number; // 유저 턴 제한 시간 (초)
  areaId?: string; // 월드맵 탐색 장소 ID
  areaName?: string; // 구역명 (예: "A1구역")
  notice?: string; // 레이드 공지 (채팅 패널 상단)
  currentMonsterIndex?: number; // 현재 타겟 몬스터 인덱스
  turnOrder?: string[]; // 턴 순서 (speed 내림차순 정렬된 ID 배열)
  currentTurnIndex?: number; // 현재 턴 인덱스
  currentTurnUserId?: string; // 현재 턴 행동자 ID
  abandonPenalty?: number; // 포기 패널티
  monsterCurrentHps?: Record<string, number>; // 각 몬스터별 현재 HP
  chatAuthorDisplayType?: 'user' | 'character' | 'characterWithUser'; // 채팅 유저 표시 방식
  isTurnPaused?: boolean; // 관리자 턴 일시 중지 여부

  // ── 턴 동기화 필드 ──────────────────────────────────────────────
  /**
   * 몬스터 턴 처리 중복 방지 잠금 키 (first-writer-wins).
   * "${monsterID}-${turnIndex}" 형식. advanceTurn 시 null 로 초기화.
   * 모든 클라이언트가 동시에 몬스터 공격을 실행하는 중복 버그를 방지한다.
   */
  monsterTurnKey?: string | null;
  /**
   * 현재 턴 시작 시각. 모든 클라이언트가 동일한 남은 시간을 표시하기 위한 기준점.
   * advanceTurn / initializeTurnOrder 호출 시 갱신.
   */
  currentTurnStartedAt?: Date | null;
}

// 레이드 전역 설정
export interface IRaidSetting {
  investigationRaidEncounterTitle?: string;
  investigationRaidEncounterMessage?: string;
  useDailyRaidParticipationLimit?: boolean;
  dailyRaidParticipationLimit?: number;
  dailyRaidParticipationResetTime?: 'Asia/Seoul';
  updatedAt?: Date;
}

// 레이드 유형 타입맵 (한글 라벨 + 설명)
export const raidTypeMap: Record<
  RAID_TYPE_VALUE,
  { label: string; description: string }
> = {
  [RAID_TYPE.COOPERATIVE]: {
    label: '협력형',
    description: '모든 참가자가 동시에 공격할 수 있습니다',
  },
  [RAID_TYPE.SEQUENTIAL]: {
    label: '순서 지정형',
    description: '참가자가 순서대로 공격합니다',
  },
  [RAID_TYPE.MASS_RAID]: {
    label: '마스레이드형',
    description: '대규모 참가자가 함께 참여하는 레이드입니다',
  },
  [RAID_TYPE.TIMELINE]: {
    label: '타임라인형',
    description: '타임라인 게시판 흐름으로 진행되는 레이드입니다',
  },
};

// 레이드 유형 라벨 (호환성 유지)
export const raidTypeLabels: Record<RAID_TYPE_VALUE, string> = {
  [RAID_TYPE.COOPERATIVE]: raidTypeMap[RAID_TYPE.COOPERATIVE].label,
  [RAID_TYPE.SEQUENTIAL]: raidTypeMap[RAID_TYPE.SEQUENTIAL].label,
  [RAID_TYPE.MASS_RAID]: raidTypeMap[RAID_TYPE.MASS_RAID].label,
  [RAID_TYPE.TIMELINE]: raidTypeMap[RAID_TYPE.TIMELINE].label,
};

// 레이드 유형 설명 (호환성 유지)
export const raidTypeDescriptions: Record<RAID_TYPE_VALUE, string> = {
  [RAID_TYPE.COOPERATIVE]: raidTypeMap[RAID_TYPE.COOPERATIVE].description,
  [RAID_TYPE.SEQUENTIAL]: raidTypeMap[RAID_TYPE.SEQUENTIAL].description,
  [RAID_TYPE.MASS_RAID]: raidTypeMap[RAID_TYPE.MASS_RAID].description,
  [RAID_TYPE.TIMELINE]: raidTypeMap[RAID_TYPE.TIMELINE].description,
};

export function normalizeRaidType(rawType?: string | null): RAID_TYPE_VALUE {
  const value = (rawType || '').toUpperCase();
  if (value === RAID_TYPE.COOPERATIVE) return RAID_TYPE.COOPERATIVE;
  if (value === RAID_TYPE.SEQUENTIAL) return RAID_TYPE.SEQUENTIAL;
  if (value === RAID_TYPE.MASS_RAID) return RAID_TYPE.MASS_RAID;
  if (value === RAID_TYPE.TIMELINE || value === 'TIMELINE_BOARD') return RAID_TYPE.TIMELINE;
  return RAID_TYPE.COOPERATIVE;
}

// 레이드 상태 라벨
export const raidStatusLabels: Record<RAID_STATUS_VALUE, string> = {
  [RAID_STATUS.SCHEDULED]: '예약됨',
  [RAID_STATUS.IN_PROGRESS]: '진행중',
  [RAID_STATUS.COMPLETED]: '완료',
};

// 레이드 상태 색상
export const raidStatusColors: Record<RAID_STATUS_VALUE, string> = {
  [RAID_STATUS.SCHEDULED]: '#f59e0b', // 주황
  [RAID_STATUS.IN_PROGRESS]: '#10b981', // 초록
  [RAID_STATUS.COMPLETED]: '#6b7280', // 회색
};
